// Cliente API para el módulo Sonos + Spotify (servicio shc-sonos, puerto 8003 vía nginx /api/sonos/)
import api from './api';
import { eventBus } from './EventBus';
import { getApiBaseUrl } from '../config';
import { getToken } from './auth';

export interface SonosArtefacto {
  nombre: string;
  icono: string;
  color_icono: string | null;
}

export interface SonosSpeaker {
  id: number;
  uid: string;
  nombre: string;
  ip: string;
  habitacion_id: number | null;
  artefacto_id: number | null;
  artefacto?: SonosArtefacto | null;
  modelo?: string | null;
  online: boolean;
  playback_state: 'playing' | 'paused' | 'idle' | string;
  current_title?: string | null;
  current_artist?: string | null;
  current_album?: string | null;
  current_album_art_url?: string | null;
  track_position?: string | null;
  track_duration?: string | null;
  play_mode?: string | null;
  playing_source_name?: string | null;
  /** Usuario SHC que inició la última reproducción vía API (null si idle / desconocido). */
  last_playback_user_id?: number | null;
  volume?: number | null;
  mute?: boolean;
  is_coordinator?: boolean;
  group_coordinator_uid?: string | null;
  crossfade_enabled?: boolean;
  enabled?: boolean;
  can_control?: boolean;
  orden?: number;
  pin_required?: boolean;
}

export interface SpotifyAccount {
  id: number;
  spotify_user_id: string;
  display_name: string | null;
  email: string | null;
  profile_image_url: string | null;
  linked_in_sonos: boolean;
  linked_in_sonos_at: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface SpotifyStatus {
  configured: boolean;
  max_accounts: number;
  count: number;
  can_add_more: boolean;
  active_account: SpotifyAccount | null;
  accounts: SpotifyAccount[];
}

export interface SpotifyPlaylist {
  uri: string;
  id: string;
  name: string;
  image_url: string | null;
  tracks: number | null;
  owner: string | null;
  added_at?: string | null;
}

export interface SpotifySavedTrack {
  id: string;
  uri: string;
  name: string;
  artist: string;
  image_url: string | null;
  duration_ms: number | null;
  added_at: string | null;
}

export type TransportAction = 'play' | 'pause' | 'stop' | 'previous' | 'next' | 'seek' | 'play_from_queue';

export interface SyncedLyricLine {
  start_sec: number;
  text: string;
}

export interface TrackLyricsResponse {
  found: boolean;
  instrumental?: boolean;
  track_name?: string;
  artist_name?: string;
  plain_lyrics?: string | null;
  synced_lines?: SyncedLyricLine[];
  source?: string;
  lrclib_id?: number | null;
}

// ---- Parlantes ----
export async function fetchSonosSpeakers(params?: { habitacion_id?: number; unassigned?: boolean }): Promise<SonosSpeaker[]> {
  const res = await api.get('/sonos/speakers', { params });
  return res.data.speakers as SonosSpeaker[];
}

/** Refresco en vivo desde Sonos LAN de parlantes en reproducción (y su grupo). */
export async function refreshActiveSonosSpeakers(): Promise<SonosSpeaker[]> {
  const res = await api.post('/sonos/speakers/refresh-active');
  return res.data.speakers as SonosSpeaker[];
}

/** Refresco LAN de parlantes concretos (habitación visible). */
export async function refreshSonosSpeakersByIds(speakerIds: number[]): Promise<SonosSpeaker[]> {
  if (!speakerIds.length) return [];
  const res = await api.post('/sonos/speakers/refresh', { speaker_ids: speakerIds });
  return (res.data?.speakers ?? []) as SonosSpeaker[];
}

export async function fetchSonosAdminSummary(): Promise<{ total: number; unassigned_count: number }> {
  const res = await api.get('/sonos/speakers/admin-summary');
  return res.data;
}

export async function patchSonosSpeaker(id: number, body: Partial<{ nombre: string; habitacion_id: number | null; artefacto_id: number; enabled: boolean; orden: number }>): Promise<SonosSpeaker> {
  const res = await api.patch(`/sonos/speakers/${id}`, body);
  return res.data;
}

export async function reorderSonosSpeakers(items: { id: number; orden: number }[]): Promise<void> {
  await api.put('/sonos/speakers/reorder', { items });
}

export async function deleteSonosSpeaker(id: number): Promise<void> {
  await api.delete(`/sonos/speakers/${id}`);
}

export async function fetchQuickPanel(id: number): Promise<any> {
  const res = await api.get(`/sonos/speakers/${id}/quick-panel`);
  return res.data;
}

/** Letras del tema (LRCLIB vía shc-sonos). Solo lectura. */
export async function fetchTrackLyrics(params: {
  speaker_id?: number;
  track_name?: string;
  artist_name?: string;
  album_name?: string;
  duration?: number;
}): Promise<TrackLyricsResponse> {
  const res = await api.get('/sonos/lyrics', { params });
  return res.data as TrackLyricsResponse;
}

// ---- Descubrimiento (admin) ----
export async function sonosDiscover(): Promise<any> {
  const res = await api.post('/sonos/discover');
  return res.data;
}

export async function sonosAddByIp(ips: string[]): Promise<any> {
  const res = await api.post('/sonos/add-by-ip', { ips });
  return res.data;
}

// ---- Control ----
export async function sonosControl(
  speaker_id: number,
  action: TransportAction | 'volume' | 'mute',
  value?: number | boolean,
): Promise<SonosSpeaker[]> {
  const res = await api.post('/sonos/control', { speaker_id, action, value });
  return (res.data?.speakers ?? []) as SonosSpeaker[];
}

export async function sonosGroup(coordinator_id: number, member_ids: number[]): Promise<void> {
  await api.post('/sonos/group', { coordinator_id, member_ids });
}

export async function sonosUngroup(speaker_id: number): Promise<void> {
  await api.post('/sonos/ungroup', { speaker_id });
}

export interface SavedSonosGroup {
  id: number;
  name: string;
  coordinator_speaker_id: number;
  speaker_ids: number[];
  volumes: Record<string, number>;
  created_at?: string | null;
  updated_at?: string | null;
  isVirtual?: boolean;
}

export function speakerIdsKey(ids: number[] | null | undefined): string {
  return [...(ids ?? [])].sort((a, b) => a - b).join(',');
}

/** Une grupos guardados en DB con chips de zona del modal ¿Dónde sonar? */
export function mergeConfigDisplayGroups(
  savedGroups: SavedSonosGroup[],
  speakers: SonosSpeaker[],
): SavedSonosGroup[] {
  const chips = buildSonosZoneGroupChips(speakers);
  const knownKeys = new Set(savedGroups.map((g) => speakerIdsKey(g.speaker_ids)));
  const merged: SavedSonosGroup[] = [...savedGroups];

  chips.forEach((chip) => {
    const key = speakerIdsKey(chip.speakerIds);
    if (knownKeys.has(key)) return;

    const volumes: Record<string, number> = {};
    chip.speakerIds.forEach((id) => {
      const sp = speakers.find((s) => s.id === id);
      volumes[String(id)] = sp?.volume ?? 0;
    });

    merged.push({
      id: 0,
      name: chip.label,
      coordinator_speaker_id: chip.coordinatorId,
      speaker_ids: chip.speakerIds,
      volumes,
      isVirtual: true,
    });
    knownKeys.add(key);
  });

  return merged.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
}

export async function fetchSavedSonosGroups(): Promise<SavedSonosGroup[]> {
  const res = await api.get('/sonos/saved-groups');
  return (res.data?.groups ?? []) as SavedSonosGroup[];
}

export async function createSavedSonosGroup(body: {
  name: string;
  coordinator_id: number;
  speaker_ids: number[];
  volumes: Record<string, number>;
}): Promise<SavedSonosGroup> {
  const res = await api.post('/sonos/saved-groups', body);
  return res.data.group as SavedSonosGroup;
}

export async function updateSavedSonosGroup(group_id: number, body: {
  name: string;
  coordinator_id: number;
  speaker_ids: number[];
  volumes: Record<string, number>;
}): Promise<SavedSonosGroup> {
  const res = await api.patch(`/sonos/saved-groups/${group_id}`, body);
  return res.data.group as SavedSonosGroup;
}

export async function deleteSavedSonosGroup(group_id: number): Promise<void> {
  await api.delete(`/sonos/saved-groups/${group_id}`);
}

export async function applySavedSonosGroup(group_id: number): Promise<{ coordinator_id: number; speakers: SonosSpeaker[] }> {
  const res = await api.post(`/sonos/saved-groups/${group_id}/apply`);
  return {
    coordinator_id: res.data.coordinator_id,
    speakers: (res.data.speakers ?? []) as SonosSpeaker[],
  };
}

export async function sonosSetEq(speaker_id: number, body: { bass?: number; treble?: number; loudness?: boolean }): Promise<void> {
  await api.patch(`/sonos/speakers/${speaker_id}/eq`, body);
}

// ---- EQ presets ----
export async function getEqPreset(speaker_id: number): Promise<{ bass: number; treble: number; loudness: boolean }> {
  const res = await api.get(`/sonos/eq-presets/${speaker_id}`);
  return res.data;
}

export async function putEqPreset(speaker_id: number, body: { bass: number; treble: number; loudness: boolean }): Promise<void> {
  await api.put(`/sonos/eq-presets/${speaker_id}`, body);
}

export type SonosEqDefaultPreset = {
  sonos_speaker_id: number;
  nombre: string;
  bass: number;
  treble: number;
  loudness: boolean;
};

export async function fetchAdminEqDefaults(): Promise<SonosEqDefaultPreset[]> {
  const res = await api.get('/sonos/admin/eq-defaults');
  return (res.data?.presets ?? []) as SonosEqDefaultPreset[];
}

export async function applyAdminEqDefaultsToAllUsers(
  presets: { sonos_speaker_id: number; bass: number; treble: number; loudness: boolean }[],
): Promise<void> {
  await api.post('/sonos/admin/eq-defaults/apply-all-users', { presets });
}

export async function listVolumePresets(): Promise<{ sonos_speaker_id: number; volume: number }[]> {
  const res = await api.get('/sonos/volume-presets');
  return (res.data?.presets ?? []) as { sonos_speaker_id: number; volume: number }[];
}

export async function getVolumePreset(speaker_id: number): Promise<number> {
  const res = await api.get(`/sonos/volume-presets/${speaker_id}`);
  return Number(res.data?.volume ?? 15);
}

export async function putVolumePreset(speaker_id: number, volume: number): Promise<void> {
  await api.put(`/sonos/volume-presets/${speaker_id}`, { volume });
}

// ---- Preferencias ----
export async function getMusicPreferences(): Promise<{
  default_crossfade: boolean;
  last_used_speaker_id: number | null;
  playlist_sort_mode: PlaylistSortMode | null;
  playlist_view_mode: PlaylistViewMode | null;
  playback_destination_kind: 'sonos' | 'local' | null;
}> {
  const res = await api.get('/sonos/preferences/music');
  return res.data;
}

export async function patchMusicPreferences(body: {
  default_crossfade?: boolean;
  last_used_speaker_id?: number;
  clear_last_used_speaker?: boolean;
  playlist_sort_mode?: PlaylistSortMode;
  playlist_view_mode?: PlaylistViewMode;
  playback_destination_kind?: 'sonos' | 'local';
}): Promise<any> {
  const res = await api.patch('/sonos/preferences/music', body);
  return res.data;
}

// ---- Cola ----
export interface SonosQueueItem {
  index?: number;
  title?: string;
  artist?: string;
  album?: string;
  uri?: string;
  image_url?: string | null;
}

const queueCache = new Map<number, SonosQueueItem[]>();
const trackImageByUri = new Map<string, string>();
const trackImageByKey = new Map<string, string>();
type PlaylistTrackIndexRow = { name: string; artists: string };
const playlistTrackIndexCache = new Map<string, PlaylistTrackIndexRow[]>();
const failedPlaylistTrackIndex = new Set<string>();
let queuedPlaylistId: string | null = null;

export function extractSpotifyPlaylistId(idOrUri?: string | null): string | null {
  if (!idOrUri) return null;
  const raw = idOrUri.trim();
  const colon = raw.match(/spotify:playlist:([A-Za-z0-9]+)/i);
  if (colon) return colon[1];
  const url = raw.match(/playlist\/([A-Za-z0-9]+)/i);
  if (url) return url[1];
  if (/^[A-Za-z0-9]{10,}$/.test(raw) && !raw.includes(':')) return raw;
  return null;
}

export function setQueuedPlaylistContext(playlistIdOrUri: string | null): void {
  queuedPlaylistId = playlistIdOrUri ? extractSpotifyPlaylistId(playlistIdOrUri) : null;
}

export function normalizeSpotifyTrackUri(uri?: string | null): string | null {
  if (!uri) return null;
  const raw = uri.trim();
  if (raw.startsWith('spotify:track:')) return raw.split('?')[0];
  try {
    const decoded = decodeURIComponent(raw);
    const match = decoded.match(/spotify:track:([A-Za-z0-9]+)/i);
    return match ? `spotify:track:${match[1]}` : null;
  } catch {
    return null;
  }
}

function trackLookupKey(title?: string | null, artist?: string | null): string {
  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  return `${norm(title ?? '')}|${norm(artist ?? '')}`;
}

export function rememberTrackImage(
  uri: string | null | undefined,
  title: string | null | undefined,
  artist: string | null | undefined,
  imageUrl: string | null | undefined,
): void {
  if (!imageUrl?.startsWith('https://')) return;
  const norm = normalizeSpotifyTrackUri(uri);
  if (norm) trackImageByUri.set(norm, imageUrl);
  if (title?.trim()) trackImageByKey.set(trackLookupKey(title, artist), imageUrl);
}

/** Tapa https ya vista en biblioteca/cola (sin ir afuera). */
export function lookupCachedTrackImage(
  uri?: string | null,
  title?: string | null,
  artist?: string | null,
): string | null {
  const norm = normalizeSpotifyTrackUri(uri);
  if (norm) {
    const byUri = trackImageByUri.get(norm);
    if (byUri) return byUri;
  }
  if (title?.trim()) {
    const byKey = trackImageByKey.get(trackLookupKey(title, artist));
    if (byKey) return byKey;
  }
  return null;
}

export function isHttpsAlbumArtUrl(url?: string | null): url is string {
  return Boolean(url && /^https:\/\//i.test(url));
}

/**
 * Prioridad: tapa local https (biblioteca) → URL remota.
 * La URL http del parlante queda como último recurso (proxy en la UI).
 */
export function resolvePreferredAlbumArt(opts: {
  remoteUrl?: string | null;
  uri?: string | null;
  title?: string | null;
  artist?: string | null;
}): string | null {
  const { remoteUrl, uri, title, artist } = opts;
  if (isHttpsAlbumArtUrl(remoteUrl)) {
    rememberTrackImage(uri, title, artist, remoteUrl);
    return remoteUrl;
  }
  const cached = lookupCachedTrackImage(uri, title, artist);
  if (cached) return cached;
  return remoteUrl ?? null;
}

export function withPreferredAlbumArt(speaker: SonosSpeaker): SonosSpeaker {
  if (!speaker.current_title && !speaker.current_album_art_url) return speaker;
  const preferred = resolvePreferredAlbumArt({
    remoteUrl: speaker.current_album_art_url,
    title: speaker.current_title,
    artist: speaker.current_artist,
  });
  if (!preferred || preferred === speaker.current_album_art_url) return speaker;
  return { ...speaker, current_album_art_url: preferred };
}

export function rememberPlaylistTracks(tracks: SpotifyPlaylistTrackRow[]): void {
  tracks.forEach((t) => rememberTrackImage(t.uri, t.name, t.artists, t.imageUrl));
  eventBus.emit('sonos:queue-images-updated');
}

export function rememberPlaylistTrackIndex(
  playlistId: string,
  tracks: SpotifyPlaylistTrackRow[],
): void {
  failedPlaylistTrackIndex.delete(playlistId);
  playlistTrackIndexCache.set(
    playlistId,
    tracks.map((t) => ({ name: t.name, artists: t.artists })),
  );
  rememberPlaylistTracks(tracks);
  eventBus.emit('sonos:playlist-track-index-updated', { playlistId });
}

export function hasPlaylistTrackIndex(playlistId: string): boolean {
  return playlistTrackIndexCache.has(playlistId);
}

export function playlistTrackIndexMatchesFilter(playlistId: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const index = playlistTrackIndexCache.get(playlistId);
  if (!index || index.length === 0) return false;
  return index.some(
    (t) => t.name.toLowerCase().includes(q) || t.artists.toLowerCase().includes(q),
  );
}

/** Reaplica portadas cacheadas (p. ej. tras abrir el modal de edición de playlist). */
export function reapplyQueueImages(items: SonosQueueItem[]): SonosQueueItem[] {
  return fillQueueItemImages(items);
}

export function getQueuedPlaylistId(): string | null {
  return queuedPlaylistId;
}

function fillQueueItemImages(items: SonosQueueItem[]): SonosQueueItem[] {
  return items.map((it) => {
    if (it.image_url?.startsWith('https://')) {
      rememberTrackImage(it.uri, it.title, it.artist, it.image_url);
      return it;
    }
    const norm = normalizeSpotifyTrackUri(it.uri);
    const key = trackLookupKey(it.title, it.artist);
    const cached = (norm && trackImageByUri.get(norm)) || trackImageByKey.get(key);
    return cached ? { ...it, image_url: cached } : it;
  });
}

export function getCachedQueue(speaker_id: number): SonosQueueItem[] | null {
  if (!queueCache.has(speaker_id)) return null;
  return fillQueueItemImages(queueCache.get(speaker_id) ?? []);
}

export function setCachedQueue(speaker_id: number, items: SonosQueueItem[]): void {
  queueCache.set(speaker_id, fillQueueItemImages(items));
}

export function invalidateQueueCache(speaker_id: number): void {
  queueCache.delete(speaker_id);
}

export async function getQueue(speaker_id: number): Promise<SonosQueueItem[]> {
  const params: Record<string, string> = {};
  if (queuedPlaylistId) params.playlist_id = queuedPlaylistId;
  const res = await api.get(`/sonos/queue/${speaker_id}`, { params });
  const items = fillQueueItemImages((res.data.queue ?? []) as SonosQueueItem[]);
  setCachedQueue(speaker_id, items);
  return items;
}

/** Misma fuente de portadas que el modal de edición de playlist. */
export async function cachePlaylistTrackImages(playlistIdOrUri: string): Promise<void> {
  const id = extractSpotifyPlaylistId(playlistIdOrUri);
  if (!id) return;
  setQueuedPlaylistContext(id);
  // Si ya abrimos la playlist en biblioteca, no volver a pedir los temas afuera.
  if (hasPlaylistTrackIndex(id)) return;
  try {
    const data = await getSpotifyPlaylistTracks(id);
    const rawItems = extractSpotifyPlaylistTrackItems(data);
    const parsed = rawItems
      .map(parseSpotifyPlaylistTrackRow)
      .filter(Boolean) as SpotifyPlaylistTrackRow[];
    rememberPlaylistTrackIndex(id, parsed);
  } catch {
    /* La cola puede pedir playlist_id al backend aunque falle el cache local */
  }
}

/** Precarga cola en segundo plano (una sola petición rápida). */
export function prefetchQueue(speaker_id: number): void {
  void getQueue(speaker_id).catch(() => undefined);
}

export async function queueAdd(speaker_id: number, body: { uri?: string; playlist_uri?: string }): Promise<void> {
  const playlistRef = body.playlist_uri
    ?? (body.uri?.includes('playlist') ? body.uri : null);
  if (playlistRef) {
    await cachePlaylistTrackImages(playlistRef);
  }
  await api.post(`/sonos/queue/${speaker_id}/add`, body);
  invalidateQueueCache(speaker_id);
}

export async function queueRemove(speaker_id: number, index: number): Promise<void> {
  await api.delete(`/sonos/queue/${speaker_id}/${index}`);
}

export async function queuePlayAt(speaker_id: number, index: number): Promise<SonosSpeaker[]> {
  try {
    const res = await api.post(`/sonos/queue/${speaker_id}/play/${index}`);
    invalidateQueueCache(speaker_id);
    return (res.data?.speakers ?? []) as SonosSpeaker[];
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status !== 404 && status !== 405) throw err;
    return sonosControl(speaker_id, 'play_from_queue', index);
  }
}

export async function queueClear(speaker_id: number): Promise<SonosSpeaker[]> {
  const res = await api.delete(`/sonos/queue/${speaker_id}`);
  invalidateQueueCache(speaker_id);
  return (res.data?.speakers ?? []) as SonosSpeaker[];
}

export async function queueReorder(speaker_id: number, from_index: number, to_index: number): Promise<void> {
  await api.put(`/sonos/queue/${speaker_id}/reorder`, { from_index, to_index });
}

export interface PlayModeResponse {
  play_mode?: string;
  shuffle?: boolean;
  repeat?: string;
  /** Movimientos hechos en la cola Sonos al activar el aleatorio (0 si no se reordenó). */
  reordered?: number;
}

// Activar aleatorio mezcla físicamente la cola en el Sonos (un comando UPnP por
// tema): en colas largas supera el timeout global de 10 s.
const PLAY_MODE_TIMEOUT_MS = 120_000;

export async function queueShuffle(
  speaker_id: number,
  shuffle: boolean,
  repeat?: 'off' | 'one' | 'all',
): Promise<PlayModeResponse> {
  const body: { shuffle: boolean; repeat?: string } = { shuffle };
  if (repeat) body.repeat = repeat;
  const res = await api.patch(`/sonos/queue/${speaker_id}/shuffle`, body, { timeout: PLAY_MODE_TIMEOUT_MS });
  return res.data;
}

export async function setPlayMode(
  speaker_id: number,
  opts: { shuffle?: boolean; repeat?: 'off' | 'one' | 'all' },
): Promise<PlayModeResponse> {
  const res = await api.patch(`/sonos/queue/${speaker_id}/shuffle`, opts, { timeout: PLAY_MODE_TIMEOUT_MS });
  return res.data;
}

/**
 * La cola Sonos cambió de orden en el backend (aleatorio SHC): invalida caché y
 * avisa a los paneles de cola para que recarguen.
 */
export function notifyQueueReordered(speakerIds: number[]): void {
  const ids = speakerIds.filter((id) => Number.isFinite(id));
  if (!ids.length) return;
  ids.forEach((id) => invalidateQueueCache(id));
  eventBus.emit('sonos:queue-reordered', { speakerIds: ids });
}

// ---- Favoritos ----
export async function getFavorites(): Promise<number[]> {
  const res = await api.get('/sonos/favorites');
  return res.data.favorites;
}

export async function getSonosFavoritesOrden(): Promise<Map<number, number>> {
  const res = await api.get('/sonos/favorites');
  const map = new Map<number, number>();
  const ordenes = res.data.ordenes as Record<string, number> | undefined;
  if (ordenes) {
    Object.entries(ordenes).forEach(([id, orden]) => map.set(Number(id), orden));
  } else {
    (res.data.favorites as number[]).forEach((id, index) => map.set(id, index));
  }
  return map;
}

export async function addFavorite(sonos_speaker_id: number): Promise<void> {
  await api.post('/sonos/favorites', { sonos_speaker_id });
}

export async function removeFavorite(speaker_id: number): Promise<void> {
  await api.delete(`/sonos/favorites/${speaker_id}`);
}

export async function updateSonosFavoritesOrder(
  orderData: { sonos_speaker_id: number; orden: number }[],
): Promise<void> {
  await api.put('/sonos/favorites/order', orderData);
}

// ---- Spotify ----
export async function getSpotifyStatus(): Promise<SpotifyStatus> {
  const res = await api.get('/sonos/spotify/status');
  return res.data;
}

export interface WebPlaybackToken {
  access_token: string;
  expires_in: number;
  scopes?: string;
  has_streaming?: boolean;
  has_modify_playback?: boolean;
  product?: string | null;
  is_premium?: boolean;
}

export async function fetchWebPlaybackToken(): Promise<WebPlaybackToken> {
  const res = await api.get('/sonos/spotify/web-playback-token');
  return res.data as WebPlaybackToken;
}

export async function fetchSpotifyMeProduct(): Promise<{ product: string | null; is_premium: boolean }> {
  const res = await api.get('/sonos/spotify/me-product');
  return res.data;
}

export async function spotifyConnectTransfer(deviceId: string, play = false): Promise<void> {
  await api.post('/sonos/spotify/connect/transfer', { device_id: deviceId, play });
}

export async function spotifyConnectPlay(body: {
  device_id?: string;
  uris?: string[];
  context_uri?: string;
  offset?: { position?: number; uri?: string };
  position_ms?: number;
}): Promise<void> {
  await api.put('/sonos/spotify/connect/play', body);
}

/** Encola un tema en el dispositivo Connect (gap local más corto). */
export async function spotifyConnectAddToQueue(uri: string, deviceId?: string): Promise<void> {
  await api.post('/sonos/spotify/connect/queue', {
    uri,
    ...(deviceId ? { device_id: deviceId } : {}),
  });
}

export async function spotifyConnectNext(deviceId?: string): Promise<void> {
  await api.post('/sonos/spotify/connect/next', deviceId ? { device_id: deviceId } : {});
}

export async function spotifyConnectPause(deviceId?: string): Promise<void> {
  await api.put('/sonos/spotify/connect/pause', deviceId ? { device_id: deviceId } : {});
}

export async function spotifyConnectSeek(positionMs: number, deviceId?: string): Promise<void> {
  await api.put('/sonos/spotify/connect/seek', {
    position_ms: positionMs,
    ...(deviceId ? { device_id: deviceId } : {}),
  });
}

export async function spotifyConnectVolume(volumePercent: number, deviceId?: string): Promise<void> {
  await api.put('/sonos/spotify/connect/volume', {
    volume_percent: volumePercent,
    ...(deviceId ? { device_id: deviceId } : {}),
  });
}

export async function spotifyLogin(): Promise<{ auth_url: string }> {
  const res = await api.get('/sonos/spotify/login');
  return res.data;
}

export async function spotifyReauth(account_id: number): Promise<{ auth_url: string }> {
  const res = await api.get(`/sonos/spotify/login/reauth/${account_id}`);
  return res.data;
}

export async function activateSpotifyAccount(account_id: number): Promise<SpotifyStatus> {
  const res = await api.patch(`/sonos/spotify/accounts/${account_id}/activate`);
  return res.data;
}

export async function setLinkedInSonos(account_id: number, confirmed: boolean): Promise<SpotifyStatus> {
  const res = await api.patch(`/sonos/spotify/accounts/${account_id}/linked-in-sonos`, { confirmed });
  return res.data;
}

export async function deleteSpotifyAccount(account_id: number): Promise<SpotifyStatus> {
  const res = await api.delete(`/sonos/spotify/accounts/${account_id}`);
  return res.data;
}

export async function getSpotifyPlaylists(favorites = false): Promise<SpotifyPlaylist[]> {
  const res = await api.get('/sonos/spotify/playlists', { params: { favorites } });
  const pls = res.data?.playlists;
  return Array.isArray(pls) ? pls : [];
}

/**
 * Dispara el chequeo liviano de cambios en Spotify (1 llamada, con debounce en
 * backend). Devuelve los playlist_id que cambiaron. Los temas de esas playlists
 * se sincronizan en background del lado servidor (caché local, PARTE IV).
 */
export async function spotifyLightSync(): Promise<{ changed: string[]; skipped?: boolean }> {
  try {
    const res = await api.post('/sonos/spotify/sync/light');
    return { changed: res.data?.changed ?? [], skipped: Boolean(res.data?.skipped) };
  } catch {
    return { changed: [], skipped: true };
  }
}

/**
 * Carga el índice de temas cacheado localmente (sin tocar Spotify) para que el
 * filtro de la Biblioteca busque por nombre de tema/artista en todas las playlists.
 */
export async function loadSpotifyTracksIndex(): Promise<void> {
  try {
    const res = await api.get('/sonos/spotify/playlists/tracks-index');
    const index = res.data?.index;
    if (!index || typeof index !== 'object') return;
    Object.entries(index as Record<string, { name: string; artists: string }[]>).forEach(
      ([playlistId, tracks]) => {
        if (Array.isArray(tracks)) {
          rememberPlaylistTrackIndex(
            playlistId,
            tracks.map((t) => ({
              uri: '', name: t.name ?? '', artists: t.artists ?? '',
              imageUrl: null, durationMs: 0,
            })),
          );
        }
      },
    );
  } catch {
    /* Sin índice local el filtro sigue funcionando por nombre/owner de playlist */
  }
}

export async function getSpotifyPlaylistDurations(playlistIds: string[]): Promise<Record<string, number>> {
  if (playlistIds.length === 0) return {};
  const res = await api.post('/sonos/spotify/playlists/durations', { ids: playlistIds });
  return res.data.durations ?? {};
}

export interface SpotifySavedTracksPage {
  tracks: SpotifySavedTrack[];
  total: number;
  has_more: boolean;
  offset: number;
}

const SAVED_TRACKS_PAGE_SIZE = 50;

export async function getSpotifySavedTracksCount(): Promise<number> {
  const res = await api.get('/sonos/spotify/saved-tracks', { params: { count_only: true } });
  return Number(res.data.total ?? 0);
}

export async function getSpotifySavedTracksPage(offset = 0, limit = SAVED_TRACKS_PAGE_SIZE): Promise<SpotifySavedTracksPage> {
  const res = await api.get('/sonos/spotify/saved-tracks', { params: { limit, offset } });
  return {
    tracks: res.data.tracks ?? [],
    total: Number(res.data.total ?? 0),
    has_more: Boolean(res.data.has_more),
    offset: Number(res.data.offset ?? offset),
  };
}

/** @deprecated Prefer getSpotifySavedTracksPage for large libraries */
export async function getSpotifySavedTracks(): Promise<SpotifySavedTrack[]> {
  const page = await getSpotifySavedTracksPage(0, 50);
  return page.tracks;
}

export async function getSpotifySavedTrackContains(ids: string[]): Promise<boolean[]> {
  if (ids.length === 0) return [];
  const res = await api.post('/sonos/spotify/saved-tracks/contains', { ids });
  return res.data.contains ?? [];
}

export async function spotifySearch(q: string, type = 'track,album,playlist,artist'): Promise<any> {
  const res = await api.get('/sonos/spotify/search', { params: { q, type } });
  return res.data;
}

export async function playSpotifyOnSpeaker(body: {
  playlist_uri: string;
  playlist_name?: string;
  playlist_image_url?: string | null;
  target_speaker_id: number;
  crossfade: boolean;
  track_offset?: number;
}): Promise<SonosSpeaker[]> {
  await cachePlaylistTrackImages(body.playlist_uri);
  const res = await api.post('/sonos/spotify/play', body);
  invalidateQueueCache(body.target_speaker_id);
  return (res.data?.speakers ?? []) as SonosSpeaker[];
}

export async function playSpotifyTrack(body: { track_uri: string; target_speaker_id: number; crossfade: boolean; add_to_queue?: boolean }): Promise<void> {
  await api.post('/sonos/spotify/play-track', body);
}

export async function getRecentPlaylists(speaker_id?: number): Promise<any[]> {
  const url = speaker_id ? `/sonos/spotify/playlists/recent/${speaker_id}` : '/sonos/spotify/playlists/recent';
  const res = await api.get(url);
  return res.data.recent;
}

// ---- Playback grupos en vivo ----
export async function liveDestination(coordinator_id: number, speaker_ids: number[]): Promise<void> {
  await api.post('/sonos/playback/live-destination', { coordinator_id, speaker_ids });
}

export async function normalizeVolume(speaker_ids: number[], coordinator_id?: number): Promise<number> {
  const res = await api.post('/sonos/playback/normalize-volume', { speaker_ids, coordinator_id });
  return res.data.volume;
}

export async function setGroupVolume(speaker_ids: number[], volume: number): Promise<number> {
  const res = await api.post('/sonos/playback/group-volume', { speaker_ids, volume });
  return res.data.volume;
}

export async function liveDestinationApply(
  coordinator_id: number,
  speaker_ids: number[],
  volumes?: Record<string, number>,
  source_coordinator_id?: number | null,
): Promise<{ coordinator_id: number; speakers: SonosSpeaker[] }> {
  // Grupos grandes (8–9 parlantes) pueden tardar; tope para no colgar la UI.
  const res = await api.post('/sonos/playback/live-destination', {
    coordinator_id,
    speaker_ids,
    volumes,
    ...(source_coordinator_id != null ? { source_coordinator_id } : {}),
  }, { timeout: 90_000 });
  return {
    coordinator_id: res.data.coordinator_id ?? coordinator_id,
    speakers: (res.data.speakers ?? []) as SonosSpeaker[],
  };
}

// ---- Spotify playlists CRUD ----
export async function createSpotifyPlaylist(body: { name: string; public?: boolean; description?: string }): Promise<{ id: string; uri: string; name: string }> {
  const res = await api.post('/sonos/spotify/playlists', body);
  return res.data;
}

export async function updateSpotifyPlaylist(playlist_id: string, body: { name?: string; description?: string }): Promise<void> {
  await api.patch(`/sonos/spotify/playlists/${playlist_id}`, body);
}

export async function deleteSpotifyPlaylist(playlist_id: string): Promise<void> {
  await api.delete(`/sonos/spotify/playlists/${playlist_id}`);
}

export interface SpotifyPlaylistTrackRow {
  uri: string;
  name: string;
  artists: string;
  imageUrl: string | null;
  durationMs: number;
}

/** Extrae items de la respuesta de playlist_items (Spotify API usa `items`, no `tracks`). */
export function extractSpotifyPlaylistTrackItems(data: unknown): unknown[] {
  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;
  const tracksField = d.tracks;
  if (tracksField && typeof tracksField === 'object' && !Array.isArray(tracksField)) {
    const nested = (tracksField as Record<string, unknown>).items;
    if (Array.isArray(nested)) return nested;
  }
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.tracks)) return d.tracks;
  return [];
}

/** Normaliza un item de playlist (track o episode) de la API de Spotify. */
export function parseSpotifyPlaylistTrackRow(raw: unknown): SpotifyPlaylistTrackRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  if (item.is_local === true) return null;

  const media = (
    item.track || item.item || (item.uri && item.type ? item : null)
  ) as Record<string, unknown> | null | undefined;
  if (!media?.uri || media.is_local === true) return null;

  const album = media.album as { images?: { url?: string }[] } | undefined;
  const show = media.show as { images?: { url?: string }[]; name?: string } | undefined;
  const images = album?.images || show?.images || [];
  const artists = (media.artists as { name?: string }[] | undefined) || [];

  return {
    uri: String(media.uri),
    name: String(media.name || ''),
    artists: artists.length > 0
      ? artists.map((a) => a.name).filter(Boolean).join(', ')
      : (show?.name ? String(show.name) : ''),
    imageUrl: images[0]?.url || null,
    durationMs: typeof media.duration_ms === 'number' ? media.duration_ms : 0,
  };
}

export async function getSpotifyPlaylistTracks(playlist_id: string): Promise<any> {
  const res = await api.get(`/sonos/spotify/playlists/${playlist_id}/tracks`);
  return res.data;
}

export async function addTracksToSpotifyPlaylist(playlist_id: string, uris: string[]): Promise<void> {
  await api.post(`/sonos/spotify/playlists/${playlist_id}/tracks`, { uris });
}

export async function removeTracksFromSpotifyPlaylist(playlist_id: string, uris: string[]): Promise<void> {
  await api.delete(`/sonos/spotify/playlists/${playlist_id}/tracks`, { data: { uris } });
}

/** Convierte índices de drag-and-drop al `insert_before` que exige Spotify. */
export function spotifyPlaylistInsertBefore(fromIndex: number, toIndex: number): number {
  return fromIndex < toIndex ? toIndex + 1 : toIndex;
}

export async function reorderSpotifyPlaylistTracks(
  playlist_id: string,
  fromIndex: number,
  toIndex: number,
  range_length = 1,
): Promise<void> {
  await api.put(`/sonos/spotify/playlists/${playlist_id}/tracks/reorder`, {
    range_start: fromIndex,
    to_index: toIndex,
    range_length,
  });
}

export async function putSpotifyPlaylistSort(order: string[]): Promise<void> {
  await api.put('/sonos/spotify/playlists/sort', { order });
}

export async function getSpotifyPlaylistSort(): Promise<string[]> {
  const res = await api.get('/sonos/spotify/playlists/sort');
  return res.data.order || [];
}

export async function spotifyLikeTrack(ids: string[]): Promise<void> {
  await api.post('/sonos/spotify/library/tracks', { ids });
}

export async function spotifyUnlikeTrack(ids: string[]): Promise<void> {
  await api.delete('/sonos/spotify/library/tracks', { data: { ids } });
}

/** Sigue una playlist (queda en la biblioteca del usuario). Metadata opcional para cache inmediata. */
export async function followSpotifyPlaylist(
  playlist_id: string,
  meta?: { uri?: string; name?: string; image_url?: string | null; owner?: string | null; tracks?: number | null },
): Promise<void> {
  await api.post(`/sonos/spotify/playlists/${playlist_id}/follow`, meta ?? {});
}

export interface AddPlaylistToLibraryMeta {
  uri?: string;
  name?: string;
  image_url?: string | null;
  owner?: string | null;
  owner_id?: string | null;
  tracks?: number | null;
}

export interface AddPlaylistToLibraryResult {
  action: 'cloned' | 'followed';
  already?: boolean;
  playlist_id?: string;
  name?: string;
  count?: number;
}

/**
 * Agrega una playlist a la biblioteca. Si es ajena (owner_id distinto al del
 * usuario), el backend la CLONA copiando sus temas a una playlist propia vía la
 * cola de un Sonos (esquiva el 403 de dev-mode). Si es propia, la sigue.
 */
export async function addPlaylistToLibrary(
  playlist_id: string,
  meta: AddPlaylistToLibraryMeta,
): Promise<AddPlaylistToLibraryResult> {
  const res = await api.post(`/sonos/spotify/playlists/${playlist_id}/add-to-library`, meta);
  return res.data;
}

/**
 * Convierte una playlist de tercero ya seguida en una propia: clona sus temas
 * y deja de seguir la original.
 */
export async function convertSpotifyPlaylist(
  playlist_id: string,
  meta: { uri?: string; name?: string },
): Promise<{ action: string; already?: boolean; playlist_id?: string; name?: string; count?: number }> {
  const res = await api.post(`/sonos/spotify/playlists/${playlist_id}/convert`, meta);
  return res.data;
}

export async function saveSpotifyAlbum(ids: string[]): Promise<void> {
  await api.post('/sonos/spotify/library/albums', { ids });
}

export async function removeSpotifyAlbum(ids: string[]): Promise<void> {
  await api.delete('/sonos/spotify/library/albums', { data: { ids } });
}

export async function getSpotifyAlbumContains(ids: string[]): Promise<boolean[]> {
  if (ids.length === 0) return [];
  const res = await api.post('/sonos/spotify/library/albums/contains', { ids });
  return res.data.contains ?? [];
}

export type PlaylistSortMode = 'alpha' | 'alpha_desc' | 'manual' | 'added_new' | 'added_old';
export type PlaylistViewMode = 'grid' | 'list';

/** Resuelve el coordinador real de un parlante (por id o uid de grupo) */
export function resolveCoordinatorSpeaker(
  speakers: SonosSpeaker[],
  speakerId: number | null,
): SonosSpeaker | null {
  if (!speakerId) return null;
  const speaker = speakers.find((s) => s.id === speakerId);
  if (!speaker) return null;
  if (!speaker.group_coordinator_uid || speaker.group_coordinator_uid === speaker.uid) {
    return speaker;
  }
  return speakers.find((s) => s.uid === speaker.group_coordinator_uid) || speaker;
}

/** Agrupa parlantes: grupos activos (coordinador + miembros) y zonas sueltas */
export function groupSpeakersForUi(speakers: SonosSpeaker[]): {
  groups: { coordinator: SonosSpeaker; members: SonosSpeaker[] }[];
  loose: SonosSpeaker[];
} {
  const byUid = new Map(speakers.map((s) => [s.uid, s]));
  const membersByCoord = new Map<string, SonosSpeaker[]>();

  speakers.forEach((s) => {
    if (s.group_coordinator_uid && s.group_coordinator_uid !== s.uid) {
      const list = membersByCoord.get(s.group_coordinator_uid) || [];
      list.push(s);
      membersByCoord.set(s.group_coordinator_uid, list);
    }
  });

  const groups: { coordinator: SonosSpeaker; members: SonosSpeaker[] }[] = [];
  const loose: SonosSpeaker[] = [];
  const processed = new Set<number>();

  membersByCoord.forEach((members, coordUid) => {
    const coordinator = byUid.get(coordUid);
    if (coordinator) {
      groups.push({ coordinator, members });
      processed.add(coordinator.id);
      members.forEach((m) => processed.add(m.id));
    } else {
      members.forEach((m) => {
        if (!processed.has(m.id)) {
          loose.push(m);
          processed.add(m.id);
        }
      });
    }
  });

  speakers.forEach((s) => {
    if (!processed.has(s.id)) {
      loose.push(s);
      processed.add(s.id);
    }
  });

  return { groups, loose };
}

/** Destino activo: coordinador + miembros del grupo */
export function resolveDestinationGroup(
  speakers: SonosSpeaker[],
  coordinatorId: number | null,
): SonosSpeaker[] {
  const coord = resolveCoordinatorSpeaker(speakers, coordinatorId);
  if (!coord) return [];
  const members = speakers.filter(
    (s) => s.group_coordinator_uid === coord.uid && s.uid !== coord.uid,
  );
  return [coord, ...members];
}

export function isSonosPlaying(state?: string | null): boolean {
  return state === 'playing' || state === 'transitioning';
}

/** Sesión activa en el parlante (reproduciendo o pausado con metadata). */
export function isSonosSpeakerInUse(speaker?: SonosSpeaker | null): boolean {
  if (!speaker) return false;
  if (isSonosPlaying(speaker.playback_state)) return true;
  const state = (speaker.playback_state || '').toLowerCase();
  if (state === 'paused' && Boolean(speaker.current_title?.trim())) return true;
  return false;
}

/**
 * Al abrir Audio: restaurar last_used salvo que ese parlante esté en uso
 * por otro usuario (last_playback_user_id distinto). Nunca autoelegir “el que suena”.
 */
export function resolveInitialCoordinatorId(
  speakers: SonosSpeaker[],
  lastUsedSpeakerId: number | null | undefined,
  currentUserId: number | null | undefined,
  fixedSpeakerId?: number | null,
): { coordinatorId: number | null; mustChooseSpeaker: boolean } {
  if (fixedSpeakerId != null) {
    const coord = resolveCoordinatorSpeaker(speakers, fixedSpeakerId);
    return { coordinatorId: coord?.id ?? fixedSpeakerId, mustChooseSpeaker: false };
  }
  if (lastUsedSpeakerId == null) {
    return { coordinatorId: null, mustChooseSpeaker: false };
  }
  const coord = resolveCoordinatorSpeaker(speakers, lastUsedSpeakerId);
  const target = coord ?? speakers.find((s) => s.id === lastUsedSpeakerId) ?? null;
  if (!target) {
    return { coordinatorId: null, mustChooseSpeaker: false };
  }
  if (!isSonosSpeakerInUse(target)) {
    return { coordinatorId: coord?.id ?? target.id, mustChooseSpeaker: false };
  }
  const ownerId = target.last_playback_user_id ?? null;
  // Solo bloquear si hay dueño conocido y es otro usuario.
  // Sin dueño (legado / app Sonos física): restaurar last_used del usuario.
  if (ownerId != null && currentUserId != null && Number(ownerId) !== Number(currentUserId)) {
    return { coordinatorId: null, mustChooseSpeaker: true };
  }
  return { coordinatorId: coord?.id ?? target.id, mustChooseSpeaker: false };
}

/** Grupo con 2+ parlantes y volúmenes distintos entre sí */
export function areGroupVolumesNormalized(speakers: SonosSpeaker[]): boolean {
  if (speakers.length <= 1) return true;
  const vols = speakers.map((s) => s.volume ?? 0);
  return vols.every((v) => v === vols[0]);
}

/** Clave de zona compartida para agrupar chips (ej. "Quincho 3" → "Quincho"). */
export function sonosZoneClusterKey(nombre: string): string {
  const trimmed = nombre.trim();
  if (!trimmed) return trimmed;
  if (trimmed.includes('_')) {
    const head = trimmed.split('_')[0]?.trim();
    if (head) return head;
  }
  const withoutTrailingNum = trimmed.replace(/[\s-]+\d+.*$/i, '').trim();
  return withoutTrailingNum || trimmed;
}

export interface SonosZoneGroupChip {
  label: string;
  speakerIds: number[];
  coordinatorId: number;
}

/** Chips de zona para el modal ¿Dónde sonar? (2+ parlantes con mismo prefijo de zona). */
export function buildSonosZoneGroupChips(speakers: SonosSpeaker[]): SonosZoneGroupChip[] {
  const controllable = speakers.filter((s) => s.can_control);
  const byKey = new Map<string, SonosSpeaker[]>();

  controllable.forEach((s) => {
    const key = sonosZoneClusterKey(s.nombre);
    const list = byKey.get(key) || [];
    list.push(s);
    byKey.set(key, list);
  });

  const chips: SonosZoneGroupChip[] = [];

  byKey.forEach((list, key) => {
    if (list.length < 2) return;

    const byUid = new Map(list.map((s) => [s.uid, s]));
    let coordinator = list.find((s) => (
      list.some((m) => m.uid !== s.uid && m.group_coordinator_uid === s.uid)
    ));
    if (!coordinator) {
      coordinator = list.find((s) => {
        const coordUid = s.group_coordinator_uid;
        return coordUid != null && coordUid !== s.uid && byUid.has(coordUid);
      });
    }
    if (!coordinator) {
      [coordinator] = [...list].sort((a, b) => a.nombre.localeCompare(b.nombre));
    }

    chips.push({
      label: key,
      speakerIds: list.map((s) => s.id),
      coordinatorId: coordinator!.id,
    });
  });

  return chips.sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

// ---- Mensajes broadcast ----
export interface SonosMessagePreferences {
  speaker_ids: number[];
  volume: number;
  play_tone: boolean;
}

export async function fetchSonosMessagePreferences(): Promise<SonosMessagePreferences> {
  const res = await api.get('/sonos/messages/preferences');
  return res.data as SonosMessagePreferences;
}

export async function patchSonosMessagePreferences(
  body: Partial<SonosMessagePreferences>,
): Promise<SonosMessagePreferences> {
  const res = await api.patch('/sonos/messages/preferences', body);
  return res.data as SonosMessagePreferences;
}

export async function broadcastSonosMessage(opts: {
  speakerIds: number[];
  volume: number;
  playTone: boolean;
  text?: string;
  audioBlob?: Blob;
  lang?: string;
}): Promise<void> {
  const form = new FormData();
  form.append('speaker_ids', JSON.stringify(opts.speakerIds));
  form.append('volume', String(opts.volume));
  form.append('play_tone', opts.playTone ? 'true' : 'false');
  if (opts.text) form.append('text', opts.text);
  if (opts.lang) form.append('lang', opts.lang);
  if (opts.audioBlob) {
    form.append('audio_file', opts.audioBlob, 'message.webm');
  }

  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 20000);
  try {
    const res = await fetch(`${getApiBaseUrl()}/sonos/messages/broadcast`, {
      method: 'POST',
      headers,
      body: form,
      signal: controller.signal,
    });
    if (!res.ok) {
      let detail = await res.text();
      try {
        const parsed = JSON.parse(detail);
        detail = parsed.detail || detail;
      } catch {
        /* texto plano */
      }
      throw new Error(String(detail));
    }
  } finally {
    window.clearTimeout(timer);
  }
}
